import { useEffect, useState } from "react";
import { api, yen } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { DatePicker } from "../components/DatePicker";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { TrendingUp, Wallet, Percent } from "lucide-react";
import { jstToday } from "../lib/jst";

const Kpi = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm text-muted-foreground">{label}</p>
      <Icon size={18} className="text-primary" />
    </div>
    <p className="num text-2xl lg:text-3xl font-semibold">{value}</p>
  </div>
);

export default function Laba() {
  const { t } = useI18n();
  const today = jstToday();
  const [start, setStart] = useState(today.slice(0, 8) + "01");
  const [end, setEnd] = useState(today);
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/reports/profit", { params: { start, end } })
      .then(({ data: d }) => setData(d));
  }, [start, end]);

  if (!data) return <div className="p-8 text-muted-foreground">{t("loading")}</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold">{t("profit_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("profit_sub")}</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("from")}</p>
            <DatePicker
              testid="profit-start-input"
              value={start}
              onChange={setStart}
              placeholder={t("from")}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("to")}</p>
            <DatePicker
              testid="profit-end-input"
              value={end}
              onChange={setEnd}
              placeholder={t("to")}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label={t("revenue")} value={yen(data.total_revenue)} icon={TrendingUp} testid="kpi-revenue" />
        <Kpi label={t("cost")} value={yen(data.total_cost)} icon={Wallet} testid="kpi-cost" />
        <Kpi label={t("profit")} value={yen(data.total_profit)} icon={TrendingUp} testid="kpi-profit" />
        <Kpi label={t("margin")} value={`${data.margin.toFixed(1)}%`} icon={Percent} testid="kpi-margin" />
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("category")}</TableHead>
                <TableHead className="text-right">{t("qty_sold")}</TableHead>
                <TableHead className="text-right">{t("revenue")}</TableHead>
                <TableHead className="text-right">{t("cost")}</TableHead>
                <TableHead className="text-right">{t("profit")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground text-center py-8">
                    {t("no_data")}
                  </TableCell>
                </TableRow>
              )}
              {data.items.map((i) => (
                <TableRow key={i.name} data-testid={`profit-row-${i.name}`}>
                  <TableCell className="font-medium">{i.name}</TableCell>
                  <TableCell className="text-muted-foreground">{i.category}</TableCell>
                  <TableCell className="text-right num">{i.qty}</TableCell>
                  <TableCell className="text-right num">{yen(i.revenue)}</TableCell>
                  <TableCell className="text-right num text-muted-foreground">
                    {yen(i.cost)}
                  </TableCell>
                  <TableCell
                    className={`text-right num font-semibold ${
                      i.profit >= 0 ? "text-emerald-600" : "text-red-600"
                    }`}
                  >
                    {yen(i.profit)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
